<template>
  <article class="text-sm max-w-[338px]">
    <header class="w-full">
      <div
        class="flex gap-2 items-center w-full font-medium leading-none whitespace-nowrap text-zinc-800"
      >
        <img
          :src="overviewIcon"
          class="object-contain shrink-0 self-stretch my-auto w-6 aspect-square"
          alt="Overview icon"
        />
        <h1 class="self-stretch my-auto">Overview</h1>
      </div>
      <p class="mt-3 leading-5 text-gray-500">
        Here are pre-created resources that you can share internally and use for
        enablement and training.
      </p>
    </header>

    <div
      v-if="showError"
      class="mt-3 p-2 bg-red-100 text-red-700 rounded-md text-xs"
    >
      {{ errorMessage }}
    </div>

    <ResourceSection
      title="Download Program Overview"
      @card-click="handleCardClick"
      @download-click="handleDownloadClick"
    />
  </article>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import ResourceSection from "./ResourceSection.vue";
import { ResourceData } from "@/types/resource_types";
import { ApolloError, gql } from "@apollo/client/core";
import { useMutation } from "@vue/apollo-composable";
import overviewIcon from "@/assets/shared/overview-icon.svg";

const PROGRAM_OVERVIEW_MUTATION = gql`
  mutation programOverview($input: ProgramOverviewInput!) {
    programOverview(input: $input) {
      success
      errors
      url
    }
  }
`;

export default defineComponent({
  name: "ResourcesOverview",
  components: {
    ResourceSection,
  },
  props: {
    programId: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const errorMessage = ref("");
    const showError = ref(false);
    const {
      mutate: programOverview,
      onDone: programOverviewDone,
      onError: programOverviewError,
    } = useMutation(PROGRAM_OVERVIEW_MUTATION);

    const downloadFile = (url: string): void => {
      try {
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("target", "_blank");

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (err) {
        errorMessage.value = "An error occurred while downloading the file.";
        showError.value = true;
      }
    };

    programOverviewDone((result) => {
      const response = result.data.programOverview;

      if (response && response.success) {
        if (response.url) {
          downloadFile(response.url);
        }
      } else if (response.errors && response.errors.length) {
        errorMessage.value = response.errors[0];
        showError.value = true;
      }
    });

    programOverviewError((error: ApolloError) => {
      errorMessage.value =
        "An error occurred while generating the program overview.";
      showError.value = true;
    });
    const generateProgramOverview = (data: ResourceData) => {
      showError.value = false;
      errorMessage.value = "";
      programOverview({
        input: {
          programId: props.programId,
          resourceType: data.type,
        },
      });
    };
    const handleCardClick = (data: ResourceData): void => {
      generateProgramOverview(data);
    };

    const handleDownloadClick = (data: ResourceData): void => {
      generateProgramOverview(data);
    };

    return {
      overviewIcon,
      handleCardClick,
      handleDownloadClick,
      showError,
      errorMessage,
    };
  },
});
</script>
